import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { useMediaQuery } from 'react-responsive';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import { Page, VideoPlayerHandle } from 'src/components';
import Notification from 'src/apps/Tools/Notification';
import { MeApi, UserDictionaryApi } from 'src/api';
import { Learnable, SessionType, WebSessionType } from 'src/api/learningSessions/types';
import correctAnswerSound from 'src/apps/Buddy/PronunciationBuddy/PronunciationViews/assets/correct_answer.mp3';
import { ROUTES } from 'src/constants/routes.const';
import { ONE_MINUTE } from 'src/constants/times.const';
import { useUserContext } from 'src/context/user';
import { useLearningToggleSettings } from 'src/hooks';
import { useEligibility } from 'src/hooks/useEligibility';
import { practiceKey, useBlobNavbarCounts } from 'src/apps/DashboardFrame/useBlobNavbarCounts';
import { DashboardHeader } from 'src/apps/DashboardFrame/DashboardHeader';
import useSafePush from 'src/hooks/useSafePush';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import { getLanguagePair } from 'src/utils/language/getLanguagePair';
import { dailyNewWordsKey } from 'src/hooks/useDailyNewWords';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import theme from 'src/utils/theme';
import { SLOW_VIDEO_SPEED } from 'src/utils/videoSpeed';
import { pauseAllVideos } from 'src/apps/LearningSession/utils/pauseAllVideos';
import { NextActionMethod, useNextAction } from 'src/apps/LearningSession/hooks/useNextAction';
import type { NextActionState } from 'src/apps/LearningSession/Common/NextActionButton';
import { useTrackCardScreenView } from 'src/apps/LearningSession/hooks/useTrackCardScreenView';
import { BaseSessionErrorModal } from 'src/apps/LearningSession/Common/BaseSession/BaseSessionErrorModal';
import { useExitSession } from 'src/apps/LearningSession/Common/BaseSession/LearningSessionHeader/useExitSession';
import { isExploreDifferentVocabModalSuppressed } from 'src/apps/LearningSession/Common/ExploreDifferentVocabModal/exploreDifferentVocabModalStorage';
import {
  applyShowAnswerBeforeTestToResponse,
  StandardSessionProps,
} from 'src/apps/LearningSession/StandardSession';
import type { VideoActorFace } from 'src/apps/LearningSession/Common/learnableCards/utils';
import pointsUpdateSound from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/points-update.mp3';
import { LocalProfile } from '../Common/LocalProfile';
import { useActorProfile } from '../Common/LocalProfile/useActorProfile';
import { SessionProgress } from './SessionProgress';
import { VideoPanel } from './VideoPanel';
import { AudioPanel } from './AudioPanel';
import { VideoRail } from './VideoRail';
import { PresentationContent } from './PresentationContent';
import { TestContent } from './TestContent';
import { SkipModal } from './SkipModal';
import { TooEasyModal } from './TooEasyModal';
import { MobileTopBar } from './MobileTopBar';
import { SwipeCard } from './SwipeCard';
import {
  getBrowsableMedia,
  getLearnableMedia,
  getMediaTrackingType,
  getSessionMedia,
  hasPlayableMedia,
  selectLearnableVideo,
} from './media';
import { MediaPager } from './MediaPager';
import { shouldPopBloom } from './LearnableWithFlower/flowerPose';
import { BLOB_TRACKING_DATA, useBlobSessionTracking } from './tracking';
import * as S from './styles';

const BLOB_SESSION_TYPES: readonly WebSessionType[] = [
  SessionType.LEARN,
  SessionType.REVIEW,
  SessionType.MIXED,
];

export const isBlobSessionType = (sessionType: WebSessionType): boolean =>
  BLOB_SESSION_TYPES.includes(sessionType);

const TOO_EASY_PROMPT_THRESHOLD = 2;

const canPromptTooEasyWordlist = (sessionType: WebSessionType): boolean =>
  sessionType === SessionType.LEARN || sessionType === SessionType.MIXED;

const SKIP_OPTIONS_LOCATION = 'learning_session_skip_options';

const BLOB_SESSION_SCREEN = 'learning_session';

const SWIPEABLE_STATES: NextActionState[] = ['Viewed', 'Correct', 'Incorrect', 'NearlyCorrect'];

export type BlobSessionProps = StandardSessionProps & {
  learnable: Learnable;
  sessionItemCount: number;
  onItemSkipped: () => void;
  /** Stops a test's feedback moving on by itself, so the learner continues when ready */
  onHoldFeedback?: () => void;
  /** Whether the learnable was due for review when its card came up */
  isDueForReview?: boolean;
  /** Blob or scenario title from the session source; used by the too-easy modal */
  sessionSourceName: string | null;
};

export const BlobSession: FC<BlobSessionProps> = ({
  languagePairId,
  sessionType,
  error,
  growthLevel,
  learnableId,
  onFeedbackFinished,
  progressSession,
  sendCurrentLearningProgress,
  sessionPercentComplete,
  title,
  card,
  learningSessionDispatch,
  onItemIgnored,
  onItemDifficultyChanged,
  currentLearnableProgressPreferences,
  definition,
  item,
  sessionSourceType,
  sessionSourceId,
  sessionSourceName,
  markPreviousAnswerCorrect,
  analyticsSessionId,
  learnable,
  sessionItemCount,
  onItemSkipped,
  onHoldFeedback,
  isDueForReview = false,
}) => {
  const { user } = useUserContext();
  const { safePush } = useSafePush();
  const { isFeatureShown } = useLearningToggleSettings();
  const { eligibility, isEligibleForPathways } = useEligibility(languagePairId);
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const hasFinePointer = useMediaQuery({ query: '(any-pointer: fine)' });
  const queryClient = useQueryClient();
  // Presenting a new word counts it towards today's goal, but the backend only hears about it
  // when the session ends, so the navbar subtracts the words presented so far itself
  const [presentedLearnableIds, setPresentedLearnableIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  if (card.data.kind === 'Presentation' && !presentedLearnableIds.has(learnableId)) {
    setPresentedLearnableIds(new Set(presentedLearnableIds).add(learnableId));
  }
  // Likewise, answering a test on a word that was due takes it off the review count. Due-ness
  // is noted when the word first comes up, as its progress moves on once it's answered
  const [dueLearnableIds, setDueLearnableIds] = useState<ReadonlySet<number>>(() => new Set());
  const [reviewedLearnableIds, setReviewedLearnableIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  if (isDueForReview && !dueLearnableIds.has(learnableId)) {
    setDueLearnableIds(new Set(dueLearnableIds).add(learnableId));
  }
  if (
    card.data.kind === 'Test' &&
    card.responseState !== 'Unanswered' &&
    dueLearnableIds.has(learnableId) &&
    !reviewedLearnableIds.has(learnableId)
  ) {
    setReviewedLearnableIds(new Set(reviewedLearnableIds).add(learnableId));
  }
  const sessionProgress = useMemo(
    () => ({
      newWordsStarted: presentedLearnableIds.size,
      wordsReviewed: reviewedLearnableIds.size,
    }),
    [presentedLearnableIds.size, reviewedLearnableIds.size],
  );
  const { counts: navbarCounts } = useBlobNavbarCounts(languagePairId, isMobile, sessionProgress);
  const { data: userLanguages, isLoading: isUserLanguagesLoading } = useQuery({
    queryKey: ['userLanguages'],
    queryFn: () => MeApi.getUserLanguages(),
    enabled: !isMobile,
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
  });
  const videoRef = useRef<VideoPlayerHandle | null>(null);
  // An outgoing mobile card unmounts after the incoming one mounts, so keep the newest handle
  const setVideoHandle = useCallback((handle: VideoPlayerHandle | null) => {
    if (handle) videoRef.current = handle;
  }, []);
  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const [isTooEasyModalOpen, setIsTooEasyModalOpen] = useState(false);
  const [isMarkingAsKnown, setIsMarkingAsKnown] = useState(false);
  const [slowVideoSrc, setSlowVideoSrc] = useState<string | null>(null);
  const [localProfile, setLocalProfile] = useState<{
    actor: VideoActorFace;
    isOpen: boolean;
  } | null>(null);
  const alreadyKnowCountRef = useRef(0);
  const hasShownTooEasyModalRef = useRef(false);
  const mixedCorrectAnswerCountRef = useRef(0);
  const lastChimedMixedCardKeyRef = useRef<string | null>(null);
  const prevGrowthLevelRef = useRef(growthLevel ?? 0);
  const [mixedCorrectChime, setMixedCorrectChime] = useState<{
    id: number;
    isBloom: boolean;
  } | null>(null);

  const { responseState, userAnswer, data: cardData } = card;
  const isUnansweredTest = cardData.kind === 'Test' && responseState === 'Unanswered';

  const cardKey = cardData.kind === 'Test' ? cardData.testId : `presentation-${learnableId}`;
  const videoPickKey = `${learnable.id}-${cardKey}`;
  // The learnable is a new object on every parent render (e.g. each keystroke in a typing
  // test), so the random video is picked once per card instead of whenever it changes
  const [learnableVideoPick, setLearnableVideoPick] = useState(() => ({
    key: videoPickKey,
    video: selectLearnableVideo(learnable),
  }));
  if (learnableVideoPick.key !== videoPickKey) {
    setLearnableVideoPick({ key: videoPickKey, video: selectLearnableVideo(learnable) });
  }
  const learnableVideo = learnableVideoPick.video;
  const cardMedia = useMemo(
    () => getSessionMedia(cardData, learnableVideo),
    [cardData, learnableVideo],
  );
  const learnableMedia = useMemo(() => getLearnableMedia(learnable), [learnable]);
  // Learners can hear the other native speakers whenever the answer is shown: on
  // presentation cards and after a wrong answer or "I don't know". Text/image
  // tests hide media until then so a video cannot give away the answer.
  const isShowingAnswer = cardData.kind === 'Presentation' || responseState === 'Incorrect';
  const revealedMedia =
    !hasPlayableMedia(cardMedia) && isShowingAnswer && learnableMedia[0]
      ? learnableMedia[0]
      : cardMedia;
  const browsableMedia = useMemo(
    () => getBrowsableMedia(revealedMedia, learnableMedia),
    [revealedMedia, learnableMedia],
  );
  const canBrowseMedia = isShowingAnswer && browsableMedia.items.length > 1;
  const [browsedMedia, setBrowsedMedia] = useState<{ cardKey: string; index: number } | null>(null);
  const browsedIndex = browsedMedia?.cardKey === cardKey ? browsedMedia.index : null;
  const mediaIndex = canBrowseMedia ? (browsedIndex ?? browsableMedia.startIndex) : 0;
  const mediaCount = canBrowseMedia ? browsableMedia.items.length : 1;
  const media =
    canBrowseMedia && browsedIndex !== null ? browsableMedia.items[browsedIndex] : revealedMedia;
  const videoSrc = media.kind === 'video' ? media.src : null;
  const mediaType = getMediaTrackingType(cardMedia);
  const showMedia = hasPlayableMedia(media);
  const isFromSaved = !!learnable.isFromSaved;
  const track = useBlobSessionTracking(languagePairId);

  const sendProgressAndRefreshNavbarCounts = useCallback(async () => {
    await sendCurrentLearningProgress();
    void queryClient.invalidateQueries({ queryKey: dailyNewWordsKey(languagePairId) });
    void queryClient.invalidateQueries({ queryKey: practiceKey(languagePairId) });
  }, [languagePairId, queryClient, sendCurrentLearningProgress]);

  const handleExit = useExitSession({
    sessionType,
    sendCurrentLearningProgress: sendProgressAndRefreshNavbarCounts,
    analyticsSessionId,
    trackingData: BLOB_TRACKING_DATA,
  });

  const trackCardContinued = (method: NextActionMethod, state: NextActionState) => {
    if (!isMobile || !SWIPEABLE_STATES.includes(state)) return;
    const continueMethod =
      method === 'pointer' ? (hasFinePointer ? 'continue_tap' : 'swipe_hint_tap') : method;
    track('card_continued', {
      method: continueMethod,
      card_type: cardData.kind === 'Presentation' ? 'presentation' : 'test',
    });
  };

  const { nextActionState, onClick, onKeyUp, triggerNextAction } = useNextAction({
    card,
    learningSessionDispatch,
    onFeedbackFinished,
    progressSession,
    onTriggered: trackCardContinued,
  });

  const screenViewTrackingData = useMemo(
    () => ({
      ...BLOB_TRACKING_DATA,
      media_type: mediaType,
      is_from_saved: String(isFromSaved),
    }),
    [mediaType, isFromSaved],
  );

  useTrackCardScreenView({
    cardData,
    languagePairId,
    isPro: !!user?.isPro,
    sessionSourceType,
    sessionSourceId,
    videoSrc: cardMedia.kind === 'video' ? cardMedia.src : null,
    videoActorIds: cardMedia.kind === 'video' ? cardMedia.actorIds : null,
    extraTrackingData: screenViewTrackingData,
  });

  // The skip options only open on test cards
  const testTrackingData: Record<string, string> = {
    learnable_id: String(learnableId),
    ...(cardData.kind === 'Test' && { test_type: cardData.response.templateName }),
  };

  const trackMediaPlayed = (
    trigger: 'play' | 'replay' | 'clip_change',
    playedMedia = media,
    index = mediaIndex,
  ) =>
    track('learnable_media_played', {
      learnable_id: String(learnableId),
      media_type: playedMedia.kind,
      media_index: String(index),
      media_count: String(mediaCount),
      trigger,
    });

  const handleMediaChange = (index: number) => {
    if (cardData.kind === 'Test') onHoldFeedback?.();
    setBrowsedMedia({ cardKey, index });
    trackMediaPlayed('clip_change', browsableMedia.items[index], index);
  };

  useEffect(() => {
    if (responseState === 'Processing') {
      pauseAllVideos();
    }
  }, [responseState]);

  const playMixedCorrectChime = useCallback(
    (chimeKey: string) => {
      if (sessionType !== SessionType.MIXED) {
        return;
      }
      if (lastChimedMixedCardKeyRef.current === chimeKey) {
        return;
      }
      lastChimedMixedCardKeyRef.current = chimeKey;
      const currentLevel = growthLevel ?? 0;
      const isBloom = shouldPopBloom(prevGrowthLevelRef.current, currentLevel);
      mixedCorrectAnswerCountRef.current += 1;
      setMixedCorrectChime({ id: mixedCorrectAnswerCountRef.current, isBloom });
    },
    [growthLevel, sessionType],
  );

  useEffect(() => {
    if (responseState !== 'Correct') {
      return;
    }
    playMixedCorrectChime(cardKey);
  }, [cardKey, playMixedCorrectChime, responseState]);

  useEffect(() => {
    prevGrowthLevelRef.current = growthLevel ?? 0;
  }, [growthLevel]);

  useEffect(() => {
    setIsSkipModalOpen(false);
    setLocalProfile(null);
  }, [learnableId]);

  const countAlreadyKnow = () => {
    if (
      !canPromptTooEasyWordlist(sessionType) ||
      hasShownTooEasyModalRef.current ||
      !isEligibleForPathways
    ) {
      return;
    }

    alreadyKnowCountRef.current += 1;
    if (alreadyKnowCountRef.current >= TOO_EASY_PROMPT_THRESHOLD) {
      hasShownTooEasyModalRef.current = true;
      if (!isExploreDifferentVocabModalSuppressed()) {
        setIsTooEasyModalOpen(true);
      }
    }
  };

  const markAsKnown = async (placement: 'rail' | 'skip_modal') => {
    /* istanbul ignore if: guards against a double submit while the request is in flight */
    if (isMarkingAsKnown) return;
    setIsMarkingAsKnown(true);
    setIsSkipModalOpen(false);

    const wordlistId = getActiveWordlistId();
    track('clicked_i_already_know_this', {
      is_pro_user: String(!!user?.isPro),
      placement,
      ...(wordlistId && { wordlist_id: wordlistId }),
    });
    learningSessionDispatch({ type: 'TOGGLE_IGNORED', learnableId });

    try {
      // Awaited so the ignore lands before the learning_sessions/end request
      await UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, { known: true });
      onItemIgnored();
      countAlreadyKnow();
    } catch (markAsKnownError) {
      /* istanbul ignore next: unexpected error */
      console.error({ error: markAsKnownError });
    } finally {
      setIsMarkingAsKnown(false);
    }
  };

  const handleIKnowThis = () => {
    if (isUnansweredTest) {
      setIsSkipModalOpen(true);
      track('screen_viewed', testTrackingData, SKIP_OPTIONS_LOCATION);
      return;
    }
    void markAsKnown('rail');
  };

  const handleSkip = () => {
    track('clicked_skip', testTrackingData, SKIP_OPTIONS_LOCATION);
    setIsSkipModalOpen(false);
    onItemSkipped();
  };

  const handleSkipModalAlreadyKnow = () => {
    track('clicked_i_already_know_this', testTrackingData, SKIP_OPTIONS_LOCATION);
    void markAsKnown('skip_modal');
  };

  const handleSkipModalClose = () => {
    track('skip_options_dismissed', testTrackingData, SKIP_OPTIONS_LOCATION);
    setIsSkipModalOpen(false);
  };

  const isDifficult = !!currentLearnableProgressPreferences?.difficult;
  const isPlayingSlowly = !!videoSrc && slowVideoSrc === videoSrc;

  const toggleDifficult = () => {
    const nextIsDifficult = !isDifficult;
    track('clicked_mark_difficult', {
      action_type: nextIsDifficult ? 'mark_difficult' : 'unmark_difficult',
      learnable_id: String(learnableId),
      card_type: cardData.kind === 'Presentation' ? 'presentation' : 'test',
    });
    UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
      difficult: nextIsDifficult,
    }).catch(toggleDifficultError => {
      console.error({ error: toggleDifficultError });
    });
    learningSessionDispatch({ type: 'TOGGLE_DIFFICULT', learnableId });
    onItemDifficultyChanged(nextIsDifficult);
  };

  const replayVideo = () => {
    trackMediaPlayed('replay');
    setSlowVideoSrc(null);
    videoRef.current?.replayAtSpeed(1);
  };

  const replayVideoSlowly = () => {
    const wordlistId = getActiveWordlistId();
    track('lwl_speed_button_clicked', {
      is_pro_user: String(!!user?.isPro),
      learnable_id: String(learnableId),
      ...(videoSrc && { video_src: videoSrc }),
      ...(wordlistId && { wordlist_id: wordlistId }),
    });
    setSlowVideoSrc(videoSrc);
    videoRef.current?.replayAtSpeed(SLOW_VIDEO_SPEED);
  };

  const openLocalProfile = (actor: VideoActorFace) => {
    pauseAllVideos();
    setLocalProfile({ actor, isOpen: true });
  };

  const handleChangeWordlist = () => {
    setIsTooEasyModalOpen(false);
    void (async () => {
      await sendProgressAndRefreshNavbarCounts();
      void safePush(
        stringifyUrl(ROUTES.editPath, {
          language_pair_id: languagePairId,
        }),
      );
    })();
  };

  const railActor = media.kind === 'video' ? media.actorFace : null;
  const { data: railActorProfile } = useActorProfile(languagePairId, railActor?.id ?? null);
  const hasRailActorVideos = !!railActorProfile?.videos.length;

  const getRailLocal = (src: string, actor: VideoActorFace) => ({
    name: actor.name,
    facePictureSrc: actor.facePictureSrc,
    videoSrc: src,
    onClick: () => openLocalProfile(actor),
  });

  const renderRail = ({ inline = false, light = false } = {}) => (
    <VideoRail
      local={
        railActor && videoSrc && hasRailActorVideos ? getRailLocal(videoSrc, railActor) : undefined
      }
      onReplay={videoSrc ? replayVideo : undefined}
      slow={videoSrc ? { isPlayingSlowly, onReplaySlowly: replayVideoSlowly } : undefined}
      difficult={{ isDifficult, onToggle: toggleDifficult }}
      onIKnowThis={handleIKnowThis}
      isIKnowThisDisabled={isMarkingAsKnown}
      inline={inline}
      light={light}
    />
  );

  const renderMediaPanel = (rail?: ReactNode) =>
    media.kind === 'video' ? (
      <VideoPanel
        key={`${cardKey}-${media.src}`}
        ref={setVideoHandle}
        src={media.src}
        autoPlay={media.autoPlay}
        rail={rail}
        onPlayButtonClick={() => {
          setSlowVideoSrc(null);
          trackMediaPlayed('play');
        }}
        onEnded={() => setSlowVideoSrc(null)}
      />
    ) : (
      <AudioPanel
        key={`${cardKey}-${media.src}`}
        src={media.src}
        autoPlay={media.autoPlay}
        rail={rail}
        dark={isMobile}
        onPlay={() => trackMediaPlayed('play')}
      />
    );

  const mediaPager = canBrowseMedia ? (
    <MediaPager index={mediaIndex} count={mediaCount} onChange={handleMediaChange} />
  ) : null;

  const renderCardContent = (overlay: boolean) => {
    if (error) {
      return <BaseSessionErrorModal sessionType={sessionType} error={error} />;
    }
    if (cardData.kind === 'Presentation') {
      return (
        <PresentationContent
          cardData={cardData}
          learnableId={learnableId}
          languagePairId={languagePairId}
          growthLevel={growthLevel ?? 0}
          showLiteralTranslation={isFeatureShown('literalTranslation')}
          showRomanization={isFeatureShown('romanization')}
          onContinueClick={onClick}
          onContinueKeyUp={onKeyUp}
          onMarkPreviousAnswerCorrect={() => {
            playMixedCorrectChime(`marked-correct-${learnableId}`);
            markPreviousAnswerCorrect(learnableId);
            progressSession();
          }}
          overlay={overlay}
          isFromSaved={isFromSaved}
        />
      );
    }
    return (
      <TestContent
        key={cardData.testId}
        cardData={cardData}
        response={applyShowAnswerBeforeTestToResponse(
          cardData.response,
          sessionType,
          isFeatureShown('showAnswerBeforeTest'),
        )}
        responseState={responseState}
        userAnswer={userAnswer}
        learningSessionDispatch={learningSessionDispatch}
        definition={definition}
        item={item}
        growthLevel={growthLevel ?? 0}
        learnableId={learnableId}
        showLiteralTranslation={isFeatureShown('literalTranslation')}
        showRomanization={isFeatureShown('romanization')}
        nextActionState={nextActionState}
        onNextClick={onClick}
        onNextKeyUp={onKeyUp}
        overlay={overlay}
      />
    );
  };

  const phrase = typeof item?.value === 'string' ? item.value : '';

  return (
    <Page footerStyle="none">
      <Notification />
      <Head>
        <title>{title} - Memrise</title>
      </Head>
      {mixedCorrectChime !== null && (
        // Decorative correct-answer SFX; captions are not applicable.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          key={`mixed-correct-${mixedCorrectChime.id}`}
          autoPlay
          preload="auto"
          src={mixedCorrectChime.isBloom ? pointsUpdateSound : correctAnswerSound}
          data-testid={
            mixedCorrectChime.isBloom
              ? 'mixed-session-milestone-sound'
              : 'mixed-session-correct-sound'
          }
        />
      )}
      {isMobile ? (
        <S.MobileStage $hasMedia={showMedia} data-testid="blob-session">
          <AnimatePresence initial={false}>
            <SwipeCard
              key={cardKey}
              canSwipe={SWIPEABLE_STATES.includes(nextActionState)}
              onSwipeUp={() => triggerNextAction('swipe')}
            >
              {showMedia ? (
                <>
                  {renderMediaPanel()}
                  {mediaPager && <S.MobilePagerSlot>{mediaPager}</S.MobilePagerSlot>}
                  <S.MobileOverlay>
                    <S.MobileCardBody $railBeside={cardData.kind === 'Presentation'}>
                      <S.MobileRail>{renderRail({ inline: true })}</S.MobileRail>
                      <S.MobileContent>{renderCardContent(true)}</S.MobileContent>
                    </S.MobileCardBody>
                  </S.MobileOverlay>
                </>
              ) : (
                <S.MobileNoMedia>
                  <S.MobileCardBody $railBeside={cardData.kind === 'Presentation'}>
                    <S.MobileRail>{renderRail({ inline: true, light: true })}</S.MobileRail>
                    <S.MobileContent>{renderCardContent(false)}</S.MobileContent>
                  </S.MobileCardBody>
                </S.MobileNoMedia>
              )}
            </SwipeCard>
          </AnimatePresence>
          <MobileTopBar
            percentComplete={sessionPercentComplete}
            itemCount={sessionItemCount}
            counts={navbarCounts}
            onExit={handleExit}
            light={!showMedia}
          />
        </S.MobileStage>
      ) : (
        <S.DesktopStage>
          <DashboardHeader
            languagePairId={languagePairId}
            languagePair={getLanguagePair(userLanguages, languagePairId)}
            isLanguagePairLoading={isUserLanguagesLoading}
            isBlobExperience
            isSupportedLanguagePairBuddies={eligibility?.eligibleForAiBuddies}
            showLogo
            screen={BLOB_SESSION_SCREEN}
            sessionProgress={sessionProgress}
          />
          <S.ProgressRow>
            <SessionProgress
              percentComplete={sessionPercentComplete}
              itemCount={sessionItemCount}
              onExit={handleExit}
            />
          </S.ProgressRow>
          {isFromSaved && cardData.kind === 'Presentation' ? (
            <S.SavedLayout data-testid="blob-session">
              <S.SavedCard>
                {showMedia && (
                  <S.SavedMedia>
                    {renderMediaPanel()}
                    {mediaPager && <S.PagerSlot>{mediaPager}</S.PagerSlot>}
                  </S.SavedMedia>
                )}
                {renderRail({ inline: true, light: true })}
                <S.SavedContent>{renderCardContent(false)}</S.SavedContent>
              </S.SavedCard>
            </S.SavedLayout>
          ) : showMedia ? (
            <S.Layout data-testid="blob-session">
              <S.MediaColumn>
                {renderMediaPanel(renderRail())}
                {mediaPager && <S.PagerSlot>{mediaPager}</S.PagerSlot>}
              </S.MediaColumn>
              <S.ContentColumn>
                <S.CardArea>{renderCardContent(false)}</S.CardArea>
              </S.ContentColumn>
            </S.Layout>
          ) : (
            <S.ContentOnlyLayout data-testid="blob-session">
              <S.ContentOnlyCard>
                {renderRail({ inline: true, light: true })}
                <S.ContentOnlyArea>{renderCardContent(false)}</S.ContentOnlyArea>
              </S.ContentOnlyCard>
            </S.ContentOnlyLayout>
          )}
        </S.DesktopStage>
      )}
      <SkipModal
        isOpen={isSkipModalOpen}
        phrase={phrase}
        onSkip={handleSkip}
        onAlreadyKnow={handleSkipModalAlreadyKnow}
        onClose={handleSkipModalClose}
      />
      {localProfile && (
        <LocalProfile
          isOpen={localProfile.isOpen}
          onClose={() => setLocalProfile({ ...localProfile, isOpen: false })}
          languagePairId={languagePairId}
          actor={localProfile.actor}
        />
      )}
      <TooEasyModal
        isOpen={isTooEasyModalOpen}
        languagePairId={languagePairId}
        sessionSourceType={sessionSourceType}
        sessionSourceName={sessionSourceName}
        onKeepGoing={() => setIsTooEasyModalOpen(false)}
        onChangeWordlist={handleChangeWordlist}
      />
    </Page>
  );
};
